import { Percentage } from '@sparkdotfi/common-universal'
import { cva, VariantProps } from 'class-variance-authority'

import { formatPercentage } from '@/domain/common/format'
import { ReserveStatus } from '@/features/sparklend/common/data/market-info/reserve-status'
import { ApyDetails } from '@/features/sparklend/markets/types'
import { MobileViewOptions } from '@/ui/molecules/data-table/types'
import { SparkRewardPill } from '@/ui/molecules/spark-reward-pill/SparkRewardPill'
import { cn } from '@/ui/utils/style'

interface ApyWithRewardsCellProps extends VariantProps<typeof variants> {
  apyDetails: ApyDetails
  reserveStatus: ReserveStatus
  mobileViewOptions?: MobileViewOptions
  'data-testid'?: string
}

export function ApyWithRewardsCell({ mobileViewOptions, ...rest }: ApyWithRewardsCellProps) {
  if (mobileViewOptions?.isMobileView) {
    return (
      <div className="flex flex-row items-center justify-between">
        <div className="typography-label-4 text-secondary">{mobileViewOptions.rowTitle}</div>
        <CellContent {...rest} />
      </div>
    )
  }

  return <CellContent {...rest} />
}

type CellContentProps = Omit<ApyWithRewardsCellProps, 'mobileViewOptions'>

function CellContent({ apyDetails, reserveStatus, 'data-testid': dataTestId }: CellContentProps) {
  if (reserveStatus !== 'active') {
    return (
      <div className="flex items-center justify-end gap-1.5" data-testid={dataTestId}>
        <CellValue value={apyDetails.baseApy} dimmed />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex items-center justify-end gap-1 lg:gap-1.5" data-testid={dataTestId}>
        <CellValue value={apyDetails.baseApy} />
      </div>
      {apyDetails.sparkRewards && (
        <div className="flex flex-wrap justify-end gap-1">
          {apyDetails.sparkRewards.map((sparkReward, index) => (
            <SparkRewardPill key={index} {...sparkReward} />
          ))}
        </div>
      )}
    </div>
  )
}

interface CellValueProps extends VariantProps<typeof variants> {
  value: Percentage | undefined
  dimmed?: boolean
}

function CellValue({ value, dimmed }: CellValueProps) {
  return <div className={cn(variants({ dimmed }))}>{formatPercentage(value)}</div>
}

const variants = cva('typography-label-2 text-primary', {
  variants: {
    dimmed: {
      true: 'text-secondary/70',
    },
  },
})
